/**
 * wasmBackend.js — decode tier 3: h265web.js WASM (PLAYBACK-HANDOFF.md §8
 * point 3). The CMS already ships this bundle for live view
 * (`public/static/wasm/{h265web.js,h265web_wasm.js,h265web_wasm.wasm}`,
 * driven by `@vgps/livekit_player`'s `wasm_decoder.ts`); this backend is
 * the SAME library driven the SAME way (`init_live_mode`,
 * `demuxer_nalu_cb` per access unit), with one deliberate difference from
 * the live-view usage: `auto_play: false`.
 *
 * Why not auto_play:true (live view's setting): h265web.js's own render
 * loop free-runs on its own RAF timer once fed, REGARDLESS of
 * auto_play/pause() (handoff §8 notes) — live view has no start gate to
 * protect, but playback's 12s autostart cushion (§7.2) does, and "exactly
 * one code path may call play()" is the rule that gate depends on. So this
 * backend comes up paused and gates its own first-frame bookkeeping on
 * "at least one real access unit has been fed AND not intentionally
 * paused" — never on the library's callback alone.
 *
 * One instance per card; the worker is terminated on destroy() (handoff:
 * "terminate the worker on stop or a page full of cards leaks decoder
 * workers").
 */

function wasmBaseUrl() {
  const configured = process.env.REACT_APP_LIVEKIT_WASM_BASE_URL || '/static/wasm/';
  return configured.endsWith('/') ? configured : `${configured}/`;
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (window.H265webjsPlayer) { resolve(); return; }
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error(`failed to (re)load ${src}`)));
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

/**
 * @param {object} args
 * @param {HTMLElement} args.containerEl  element with an `id`, per h265web.js's player_id
 * @param {object} args.callbacks  onFirstFrame(), onStall(gapMs), onError(message)
 */
export function createWasmBackend({ containerEl, callbacks }) {
  const { onFirstFrame, onStall, onError } = callbacks || {};
  let player = null;
  let destroyed = false;
  let initialized = false;
  let lastRenderTs = null;
  let needsKeyframe = true;
  // Starts true: the render loop free-runs once fed regardless of our own
  // pause state (see module doc), so bookkeeping must gate on this flag
  // explicitly rather than trust the library's callback alone.
  let pausedFlag = true;
  let cfgW = 0;
  let cfgH = 0;
  let auFed = false;

  return {
    configure(cfg) {
      cfgW = cfg.width;
      cfgH = cfg.height;
      const base = wasmBaseUrl();
      return loadScript(`${base}h265web.js`).then(() => {
        if (destroyed) return undefined;
        if (!window.H265webjsPlayer) throw new Error('h265web.js did not define window.H265webjsPlayer');
        // Force the WASM ffdecoder path (not the WebCodec-backed one h265web.js
        // can also pick): with WebCodec it renders to an OffscreenCanvas in its
        // own worker, which our simple <div>-container render target cannot use.
        if (window.YLPlayerSupport) window.YLPlayerSupport.webcodec_hevc = false;
        player = window.H265webjsPlayer();
        player.build({
          player_id: containerEl.id,
          base_url: base,
          wasm_js_uri: 'h265web_wasm.js',
          wasm_wasm_uri: 'h265web_wasm.wasm',
          width: cfgW,
          height: cfgH,
          fps: cfg.fps || 25,
          // auto_play:false — see module doc. The chunk pump keeps
          // downloading/feeding regardless; resume() is the only thing
          // that ever starts this player's own presentation clock.
          auto_play: false,
          ignore_audio: true,
        });
        player.video_render_callback = () => {
          if (!auFed || pausedFlag) return; // no real picture yet, or intentionally paused
          const now = performance.now();
          if (lastRenderTs == null) {
            onFirstFrame && onFirstFrame();
          } else {
            const gap = now - lastRenderTs;
            if (gap > 250) onStall && onStall(gap);
          }
          lastRenderTs = now;
        };
        return new Promise((resolve) => {
          player.init_live_mode(cfgW, cfgH, () => { initialized = true; resolve(); });
        });
      }).catch((e) => {
        onError && onError((e && e.message) || String(e));
        throw e; // still reject -- the jsTierPump chain awaiting configure() must see the failure
      });
    },
    feed(annexB, isKey, ptsUs) {
      if (destroyed || !player || !initialized) return;
      if (needsKeyframe) {
        if (!isKey) return;
        needsKeyframe = false;
      }
      const worker = player._decoder_worker;
      if (!worker) { needsKeyframe = true; return; }
      worker.postMessage({
        type: 'demuxer_nalu_cb',
        payload: { nalu: annexB, pts: ptsUs / 1e6, dts: ptsUs / 1e6, w: cfgW, h: cfgH, isKey },
      });
      auFed = true;
    },
    pause() { pausedFlag = true; if (player) player.pause(); },
    resume() { pausedFlag = false; if (player) player.play(); },
    get paused() { return pausedFlag; },
    destroy() {
      destroyed = true;
      if (player) {
        const worker = player._decoder_worker;
        player.video_render_callback = null;
        try { player._opengl_render && player._opengl_render.render_pause && player._opengl_render.render_pause(); } catch (_) { /* ignore */ }
        try { player.release && player.release(); } catch (_) { /* ignore */ }
        window.setTimeout(() => { try { worker && worker.terminate(); } catch (_) { /* ignore */ } }, 250);
        player = null;
      }
      initialized = false;
    },
  };
}
